/**
 * Offline Trip Service
 * 
 * Manages trip data caching for offline access.
 * Caches complete trip data including expenses, bookings, and itinerary.
 */

import * as FileSystem from 'expo-file-system/legacy';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { tripStorage, metadataStorage, type OfflineMetadata } from './offlineStorage';
import { syncQueue } from './syncQueue';
import { networkMonitor } from './networkMonitor';
import type { Trip } from './trips';
import { normalizeTripForClient } from './trips';
import type { Expense } from './expenses';
import type { Booking } from './bookings';
import type { ItineraryItem } from './itinerary';
import { shouldPreserveReservationAnchoredItineraryDates } from '@/utils/itineraryReservationAnchors';
import { coerceDate, toLocalDateKey } from '@/utils/dateKeys';
import { canUserAccessTripFromStoredData } from '@/utils/tripAccess';
import { normalizeAttachments, downloadAttachmentForOffline } from './itineraryAttachments';

const TRIP_CACHE_EXPIRATION = 7 * 24 * 60 * 60 * 1000; // 7 days
const ACTIVE_TRIP_CACHE_EXPIRATION = 30 * 24 * 60 * 60 * 1000; // 30 days for active trips

/** Trip IDs for which Plan/Wallet auto trip-pack refresh must not recreate cache after user removed offline data. */
const TRIP_PACK_AUTOCACHE_DISABLED_KEY = '@tripflow_trip_pack_autocache_disabled';

async function readTripPackAutocacheDisabledSet(): Promise<Set<string>> {
  try {
    const raw = await AsyncStorage.getItem(TRIP_PACK_AUTOCACHE_DISABLED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((id): id is string => typeof id === 'string' && id.length > 0));
  } catch {
    return new Set();
  }
}

async function writeTripPackAutocacheDisabledSet(set: Set<string>): Promise<void> {
  await AsyncStorage.setItem(TRIP_PACK_AUTOCACHE_DISABLED_KEY, JSON.stringify([...set]));
}

/** Call from explicit "Download for offline" flows so auto-refresh resumes. */
export async function setTripPackAutoCacheDisabled(tripId: string, disabled: boolean): Promise<void> {
  const set = await readTripPackAutocacheDisabledSet();
  if (disabled) {
    set.add(tripId);
  } else {
    set.delete(tripId);
  }
  await writeTripPackAutocacheDisabledSet(set);
}

export async function isTripPackAutoCacheDisabled(tripId: string): Promise<boolean> {
  const set = await readTripPackAutocacheDisabledSet();
  return set.has(tripId);
}

export type CachedTripData = {
  trip: Trip;
  expenses?: Expense[];
  bookings?: Booking[];
  itinerary?: unknown[]; // ItineraryItem[]
  lastSynced: number;
  expiresAt: number;
};

export type CacheTripPackOptions = {
  force?: boolean;
};

function toDateMaybe(value: unknown): Date | null {
  return coerceDate(value);
}

function normalizeTripDates(trip: Trip): Trip {
  const anyTrip = trip as any;
  const startDate = toDateMaybe(anyTrip.startDate);
  const endDate = toDateMaybe(anyTrip.endDate);
  const createdAt = toDateMaybe(anyTrip.createdAt);
  const updatedAt = toDateMaybe(anyTrip.updatedAt);
  const lastEditedAt = toDateMaybe(anyTrip.lastEditedAt);

  const revived: Trip = {
    ...(trip as any),
    ...(startDate ? { startDate } : null),
    ...(endDate ? { endDate } : null),
    ...(createdAt ? { createdAt } : null),
    ...(updatedAt ? { updatedAt } : null),
    ...(lastEditedAt ? { lastEditedAt } : null),
  } as Trip;

  if (Array.isArray(anyTrip.destinations)) {
    revived.destinations = anyTrip.destinations.map((dest: any) => ({
      ...dest,
      ...(toDateMaybe(dest?.startDate) ? { startDate: toDateMaybe(dest.startDate)! } : null),
      ...(toDateMaybe(dest?.endDate) ? { endDate: toDateMaybe(dest.endDate)! } : null),
    }));
  }

  return normalizeTripForClient(revived);
}

async function attachOfflineReceiptCopies(tripId: string, expenses: Expense[]): Promise<Expense[]> {
  if (!networkMonitor.isOnline()) {
    return expenses.map((e) => ({ ...e }));
  }
  const base = FileSystem.documentDirectory;
  if (!base) {
    return expenses.map((e) => ({ ...e }));
  }

  const dir = `${base}tripflow_offline_receipts/`;
  await FileSystem.makeDirectoryAsync(dir, { intermediates: true }).catch(() => {});

  const out: Expense[] = [];
  for (const e of expenses) {
    const url = e.receiptUrl?.trim();
    if (!url || !/^https?:\/\//i.test(url)) {
      out.push({ ...e });
      continue;
    }
    const safeId = e.id.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 80) || 'exp';
    const ext = /\.pdf(\?|$)/i.test(url) ? 'pdf' : /\.png(\?|$)/i.test(url) ? 'png' : 'jpg';
    const dest = `${dir}${tripId}_${safeId}.${ext}`;
    try {
      const info = await FileSystem.getInfoAsync(dest);
      if (!info.exists) {
        await FileSystem.downloadAsync(url, dest);
      }
      out.push({ ...e, receiptLocalUri: dest });
    } catch (err) {
      console.warn('[OfflineTrips] Receipt cache download failed', e.id, err);
      out.push({ ...e });
    }
  }
  return out;
}

/** Remove cached receipt files under documentDirectory (e.g. on sign-out). */
export async function clearOfflineReceiptsDir(): Promise<void> {
  const base = FileSystem.documentDirectory;
  if (!base) return;
  const dir = `${base}tripflow_offline_receipts/`;
  try {
    const info = await FileSystem.getInfoAsync(dir);
    if (info.exists) {
      await FileSystem.deleteAsync(dir, { idempotent: true });
    }
  } catch {
    /* best-effort */
  }
}

/** Reset trip-pack auto-cache disabled list (e.g. on sign-out). */
export async function clearTripPackAutocacheDisabledStorage(): Promise<void> {
  try {
    await AsyncStorage.removeItem(TRIP_PACK_AUTOCACHE_DISABLED_KEY);
  } catch {
    /* best-effort */
  }
}

function tripMatchesUser(trip: Trip, userId?: string | null): boolean {
  if (!userId) {
    return true;
  }
  return canUserAccessTripFromStoredData(userId, trip);
}

/**
 * Check if a trip is active (within 7 days of start or end date)
 */
function isActiveTrip(trip: Trip): boolean {
  const now = Date.now();
  const startTime = trip.startDate.getTime();
  // Treat end dates as inclusive (end-of-day) to avoid marking trips inactive on their end date.
  const end = new Date(trip.endDate);
  if (Number.isFinite(end.getTime())) {
    end.setHours(23, 59, 59, 999);
  }
  const endTime = Number.isFinite(end.getTime()) ? end.getTime() : trip.endDate.getTime();
  const sevenDays = 7 * 24 * 60 * 60 * 1000;
  
  return (
    (now >= startTime - sevenDays && now <= startTime) || // Within 7 days before start
    (now >= startTime && now <= endTime) || // During trip
    (now >= endTime && now <= endTime + sevenDays) // Within 7 days after end
  );
}

/**
 * Partial update for the offline trip pack. Only include keys you want to replace.
 * Omitted keys keep the previous cached values.
 *
 * Use an empty array to intentionally clear a slice (e.g. `expenses: []`).
 * If you include `expenses` but set it to `undefined`, previous expenses are kept.
 *
 * When the merged expense list is non-empty and the device is online, https receipt URLs
 * are downloaded and `receiptLocalUri` is set (skipped when the file already exists).
 */
export type TripOfflinePackSlice = {
  expenses?: Expense[];
  bookings?: Booking[];
  itinerary?: unknown[];
};

function sliceHas<K extends keyof TripOfflinePackSlice>(
  slice: TripOfflinePackSlice,
  key: K,
): slice is TripOfflinePackSlice & Record<K, TripOfflinePackSlice[K]> {
  return Object.prototype.hasOwnProperty.call(slice, key);
}

/**
 * While online, download HTTPS itinerary attachments to the device (same pattern as receipt caching).
 * Cached rows get `attachments[].offlinePath` so the in-app viewer works offline.
 */
async function attachOfflineItineraryAttachmentCopies(
  itinerary: unknown[] | undefined | null,
): Promise<unknown[] | undefined> {
  if (!itinerary?.length || !networkMonitor.isOnline()) {
    return itinerary === null ? undefined : itinerary;
  }
  const out: unknown[] = [];
  for (const raw of itinerary) {
    if (!raw || typeof raw !== 'object') {
      out.push(raw);
      continue;
    }
    const item = raw as ItineraryItem;
    const atts = normalizeAttachments(item.attachments, item.documents || []);
    if (!atts.length) {
      out.push(raw);
      continue;
    }
    const nextAtts = [];
    for (const att of atts) {
      const u = (att.url || '').trim();
      if (!u || !/^https?:\/\//i.test(u)) {
        nextAtts.push(att);
        continue;
      }
      try {
        nextAtts.push(await downloadAttachmentForOffline(att));
      } catch (e) {
        console.warn('[OfflineTrips] Itinerary attachment cache failed', item.id, e);
        nextAtts.push(att);
      }
    }
    out.push({
      ...item,
      attachments: nextAtts,
      documents: nextAtts.map((a) => a.url).filter(Boolean),
    });
  }
  return out;
}

export async function cacheTripPackForOffline(
  trip: Trip,
  slice: TripOfflinePackSlice,
  options?: CacheTripPackOptions,
): Promise<boolean> {
  try {
    if (!options?.force && (await isTripPackAutoCacheDisabled(trip.id))) {
      return false;
    }

    const isActive = isActiveTrip(trip);
    const expiration = isActive ? ACTIVE_TRIP_CACHE_EXPIRATION : TRIP_CACHE_EXPIRATION;

    const existing = await tripStorage.getTripData<CachedTripData>(trip.id);

    const nextExpenses = sliceHas(slice, 'expenses')
      ? (slice.expenses ?? existing?.expenses)
      : existing?.expenses;
    const nextBookings = sliceHas(slice, 'bookings')
      ? (slice.bookings ?? existing?.bookings)
      : existing?.bookings;
    const nextItinerary = sliceHas(slice, 'itinerary')
      ? (slice.itinerary ?? existing?.itinerary)
      : existing?.itinerary;

    let expensesForCache = nextExpenses;
    if (
      networkMonitor.isOnline() &&
      expensesForCache &&
      expensesForCache.length > 0
    ) {
      expensesForCache = await attachOfflineReceiptCopies(trip.id, expensesForCache);
    }

    let itineraryForCache = nextItinerary;
    if (networkMonitor.isOnline() && itineraryForCache && itineraryForCache.length > 0) {
      itineraryForCache = await attachOfflineItineraryAttachmentCopies(itineraryForCache);
    }

    const cachedData: CachedTripData = {
      trip,
      expenses: expensesForCache,
      bookings: nextBookings,
      itinerary: itineraryForCache,
      lastSynced: Date.now(),
      expiresAt: Date.now() + expiration,
    };

    await tripStorage.setTripData(trip.id, cachedData);

    const metadata = await metadataStorage.getMetadata() || {
      lastSync: 0,
      version: '1.0.0',
      tripIds: [],
    };

    if (!metadata.tripIds.includes(trip.id)) {
      metadata.tripIds.push(trip.id);
    }

    await metadataStorage.updateMetadata({
      lastSync: Date.now(),
      tripIds: metadata.tripIds,
    });

    console.log(`[OfflineTrips] Cached trip pack for ${trip.name} (ID: ${trip.id})`);
    return true;
  } catch (error) {
    console.error(`[OfflineTrips] Error caching trip pack for ${trip.id}:`, error);
    return false;
  }
}

/**
 * Same as {@link cacheTripPackForOffline} with an empty object when the second argument is omitted.
 */
export async function cacheTripData(
  trip: Trip,
  relatedData?: TripOfflinePackSlice,
  options?: CacheTripPackOptions,
): Promise<boolean> {
  return await cacheTripPackForOffline(trip, relatedData ?? {}, options);
}

/**
 * After document offline downloads, refresh expense receipt files for affected trips.
 * Uses cached expenses when present; otherwise fetches from Firestore (online only).
 */
export async function refreshOfflineReceiptCopiesForTrips(
  tripIds: Iterable<string>,
  resolveTrip: (tripId: string) => Trip | null | undefined,
): Promise<void> {
  if (!networkMonitor.isOnline()) return;

  const seen = new Set<string>();
  for (const tripId of tripIds) {
    if (!tripId || seen.has(tripId)) continue;
    seen.add(tripId);

    const trip = resolveTrip(tripId);
    if (!trip) continue;

    try {
      const existing = await getCachedTripData(tripId);
      // Never recreate a trip pack here (e.g. after user removed offline plan); only merge receipts into an existing pack.
      if (!existing) continue;

      let expenses = existing.expenses;
      if (!expenses?.length) {
        const { fetchTripExpenses } = await import('./expenses');
        expenses = await fetchTripExpenses(tripId);
      }
      if (!expenses?.length) continue;

      await cacheTripPackForOffline(
        trip,
        {
          expenses,
          ...(existing.bookings !== undefined ? { bookings: existing.bookings } : {}),
          ...(existing.itinerary !== undefined ? { itinerary: existing.itinerary } : {}),
        },
        { force: true },
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.warn('[OfflineTrips] refreshOfflineReceiptCopiesForTrips failed', { message });
    }
  }
}

/**
 * Get cached trip data
 */
export async function getCachedTripData(tripId: string): Promise<CachedTripData | null> {
  try {
    const cached = await tripStorage.getTripData<CachedTripData>(tripId);
    
    if (!cached) {
      return null;
    }
    
    if (cached.trip) {
      cached.trip = normalizeTripDates(cached.trip);
    }

    // Check if expired
    if (cached.expiresAt && Date.now() > cached.expiresAt) {
      console.log('[OfflineTrips] Cached trip data has expired');
      await tripStorage.removeTrip(tripId);
      return null;
    }
    
    return cached;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[OfflineTrips] Error getting cached trip data', { message });
    return null;
  }
}

/**
 * Get cached trip (just the trip, not related data)
 */
export async function getCachedTrip(tripId: string): Promise<Trip | null> {
  try {
    const cachedTrip = await tripStorage.getTrip<Trip>(tripId);
    if (cachedTrip) {
      return normalizeTripDates(cachedTrip);
    }

    const cached = await getCachedTripData(tripId);
    if (cached?.trip) {
      const normalized = normalizeTripDates(cached.trip);
      // Backfill lightweight trip summaries so future startups don't deserialize full trip packs.
      await tripStorage.setTrip(tripId, normalized);
      return normalized;
    }

    return null;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[OfflineTrips] Error getting cached trip', { message });
    return null;
  }
}

/**
 * Get all cached trips
 */
export async function getCachedTrips(userId?: string | null): Promise<Trip[]> {
  try {
    const metadata = await metadataStorage.getMetadata();
    if (!metadata || !metadata.tripIds || metadata.tripIds.length === 0) {
      return [];
    }
    
    const trips: Trip[] = [];
    for (const tripId of metadata.tripIds) {
      const cached = await getCachedTrip(tripId);
      if (cached) {
        const normalized = normalizeTripDates(cached);
        if (tripMatchesUser(normalized, userId)) {
          trips.push(normalized);
        }
      }
    }
    
    return trips.sort((a, b) => {
      const aStart = a.startDate instanceof Date ? a.startDate : toDateMaybe((a as any).startDate);
      const bStart = b.startDate instanceof Date ? b.startDate : toDateMaybe((b as any).startDate);
      const aMs = aStart ? aStart.getTime() : 0;
      const bMs = bStart ? bStart.getTime() : 0;
      return aMs - bMs;
    });
  } catch (error) {
    console.error('[OfflineTrips] Error getting cached trips:', error);
    return [];
  }
}

/**
 * Queue trip update for sync
 */
export async function queueTripUpdate(
  tripId: string,
  updates: Partial<Trip>,
  userId: string,
  meta?: Record<string, unknown>,
): Promise<void> {
  try {
    // Update local cache immediately
    const cached = await getCachedTripData(tripId);
    if (cached) {
      const oldStartDate = coerceDate(cached.trip?.startDate);
      const newStartDate = coerceDate(updates?.startDate);

      const updatedTrip: Trip = {
        ...cached.trip,
        ...updates,
        // Do not bump `updatedAt` locally — that made disk cache win over Firestore on list merge.
        updatedAt: cached.trip.updatedAt ?? new Date(),
      };

      // If startDate changed, shift template itinerary items in the cached payload so the UI stays consistent offline.
      if (
        oldStartDate &&
        newStartDate &&
        !isNaN(oldStartDate.getTime()) &&
        !isNaN(newStartDate.getTime()) &&
        toLocalDateKey(oldStartDate) !== toLocalDateKey(newStartDate)
      ) {
        const MS_PER_DAY = 24 * 60 * 60 * 1000;
        const utcDayNumber = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / MS_PER_DAY;
        const deltaDays = utcDayNumber(newStartDate) - utcDayNumber(oldStartDate);

        if (deltaDays !== 0 && Array.isArray(cached.itinerary)) {
          cached.itinerary = cached.itinerary.map((raw: any) => {
            if (!raw || !raw.startDate || shouldPreserveReservationAnchoredItineraryDates(raw)) {
              return raw;
            }
            const start = raw.startDate instanceof Date ? raw.startDate : new Date(raw.startDate);
            if (isNaN(start.getTime())) {
              return raw;
            }
            const nextStart = new Date(start);
            nextStart.setDate(nextStart.getDate() + deltaDays);
            const next = { ...raw, startDate: nextStart };

            if (raw.endDate) {
              const end = raw.endDate instanceof Date ? raw.endDate : new Date(raw.endDate);
              if (!isNaN(end.getTime())) {
                const nextEnd = new Date(end);
                nextEnd.setDate(nextEnd.getDate() + deltaDays);
                next.endDate = nextEnd;
              }
            }

            return next;
          });
        }
      }
      
      await cacheTripData(
        updatedTrip,
        {
          expenses: cached.expenses,
          bookings: cached.bookings,
          itinerary: cached.itinerary,
        },
        { force: true },
      );
    }
    
    // Queue for sync if online
    if (networkMonitor.isOnline()) {
      await syncQueue.queueAction({
        type: 'update_trip',
        payload: {
          tripId,
          updates,
          meta,
        },
        tripId,
        userId,
      });
    } else {
      // Store update to be queued when online
      await syncQueue.queueAction({
        type: 'update_trip',
        payload: {
          tripId,
          updates,
          meta,
        },
        tripId,
        userId,
      });
    }
    
    console.log('[OfflineTrips] Queued trip update');
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[OfflineTrips] Error queueing trip update', { message });
    throw error;
  }
}

/**
 * Remove cached trip data
 */
export async function removeCachedTrip(tripId: string): Promise<void> {
  try {
    await tripStorage.removeTrip(tripId);
    await setTripPackAutoCacheDisabled(tripId, true);

    // Update metadata
    const metadata = await metadataStorage.getMetadata();
    if (metadata && metadata.tripIds) {
      metadata.tripIds = metadata.tripIds.filter(id => id !== tripId);
      await metadataStorage.updateMetadata({
        tripIds: metadata.tripIds,
      });
    }
    
    console.log('[OfflineTrips] Removed cached trip data');
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[OfflineTrips] Error removing cached trip data', { message });
  }
}

/**
 * Sync offline cache with the authoritative trip list from Firestore.
 * Removes cached data for any trip ID not in the current list so deleted
 * trips never reappear when loading the Plan screen from cache.
 */
export async function syncCacheWithTripIds(currentTripIds: string[]): Promise<void> {
  try {
    const metadata = await metadataStorage.getMetadata();
    if (!metadata?.tripIds?.length) return;
    const set = new Set(currentTripIds);
    const toRemove = metadata.tripIds.filter((id) => !set.has(id));
    if (toRemove.length === 0) return;
    for (const tripId of toRemove) {
      await tripStorage.removeTrip(tripId);
    }
    const keptIds = metadata.tripIds.filter((id) => set.has(id));
    await metadataStorage.updateMetadata({ tripIds: keptIds });
    if (__DEV__ && toRemove.length > 0) {
      console.log('[OfflineTrips] Synced cache: removed', toRemove.length, 'trip(s) no longer in Firestore');
    }
  } catch (error) {
    console.error('[OfflineTrips] Error syncing cache with trip list:', error);
  }
}

/**
 * Clear all cached trip data
 */
export async function clearAllCachedTrips(): Promise<void> {
  try {
    const metadata = await metadataStorage.getMetadata();
    if (metadata && metadata.tripIds) {
      for (const tripId of metadata.tripIds) {
        await setTripPackAutoCacheDisabled(tripId, true);
        await tripStorage.removeTrip(tripId);
      }
    }
    
    await metadataStorage.updateMetadata({
      tripIds: [],
      lastSync: 0,
    });
    
    console.log('[OfflineTrips] Cleared all cached trip data');
  } catch (error) {
    console.error('[OfflineTrips] Error clearing all cached trip data:', error);
  }
}

/**
 * Get last sync timestamp
 */
export async function getLastSyncTime(): Promise<number | null> {
  try {
    const metadata = await metadataStorage.getMetadata();
    const t = metadata?.lastSync;
    return typeof t === 'number' && t > 0 ? t : null;
  } catch (error) {
    console.error('[OfflineTrips] Error getting last sync time:', error);
    return null;
  }
}

/**
 * Clean up expired trip caches
 */
export async function cleanupExpiredTrips(): Promise<void> {
  try {
    const metadata = await metadataStorage.getMetadata();
    if (!metadata || !metadata.tripIds || metadata.tripIds.length === 0) {
      return;
    }
    
    const validTripIds: string[] = [];
    
    for (const tripId of metadata.tripIds) {
      const cached = await getCachedTripData(tripId);
      if (cached) {
        validTripIds.push(tripId);
      } else {
        // Cache expired or invalid, remove from metadata
        console.log(`[OfflineTrips] Removing expired trip cache: ${tripId}`);
      }
    }
    
    if (validTripIds.length !== metadata.tripIds.length) {
      await metadataStorage.updateMetadata({
        tripIds: validTripIds,
      });
    }
    
    console.log(`[OfflineTrips] Cleanup complete. Valid caches: ${validTripIds.length}`);
  } catch (error) {
    console.error('[OfflineTrips] Error cleaning up expired trips:', error);
  }
}

/**
 * Auto-cache active trips (to be called when trips are loaded)
 */
function tripUpdatedAtMs(trip: Trip): number {
  const raw = trip.updatedAt;
  if (raw instanceof Date) {
    return raw.getTime();
  }
  if (typeof raw === 'string' || typeof raw === 'number') {
    const parsed = new Date(raw).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

export async function autoCacheActiveTrips(trips: Trip[]): Promise<void> {
  try {
    const activeTrips = trips.filter(isActiveTrip);
    
    for (const trip of activeTrips) {
      if (await isTripPackAutoCacheDisabled(trip.id)) {
        continue;
      }
      const cached = await getCachedTripData(trip.id);
      const cacheExpired = !cached || Date.now() > cached.expiresAt;
      const serverIsNewer =
        cached?.trip != null && tripUpdatedAtMs(trip) > tripUpdatedAtMs(cached.trip);
      if (cacheExpired || serverIsNewer) {
        await cacheTripData(trip);
      }
    }
    
    console.log(`[OfflineTrips] Auto-cached ${activeTrips.length} active trips`);
  } catch (error) {
    console.error('[OfflineTrips] Error auto-caching active trips:', error);
  }
}

function reviveDate(v: unknown): Date | null {
  const coerced = coerceDate(v);
  if (coerced) return coerced;
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Date(v);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  return null;
}

/** Restore itinerary rows from JSON/offline storage (dates are often strings). */
export function itineraryItemsFromOfflineCache(
  raw: unknown[] | undefined | null,
  tripId: string,
): ItineraryItem[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const out: ItineraryItem[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const x = row as ItineraryItem;
    if (typeof x.id !== 'string') continue;
    if (typeof x.tripId === 'string' && x.tripId.length > 0 && x.tripId !== tripId) {
      continue;
    }
    try {
      const startDate = reviveDate(x.startDate as unknown);
      if (!startDate) continue;
      const createdAt = reviveDate(x.createdAt as unknown) ?? new Date(0);
      const updatedAt = reviveDate(x.updatedAt as unknown) ?? new Date(0);
      out.push({
        ...x,
        tripId,
        startDate,
        endDate: x.endDate != null ? reviveDate(x.endDate as unknown) ?? undefined : undefined,
        createdAt,
        updatedAt,
      });
    } catch {
      // skip malformed rows
    }
  }
  return out;
}

/** Restore expenses from JSON/offline storage. */
export function expensesFromOfflineCache(raw: unknown[] | undefined | null): Expense[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const out: Expense[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const x = row as Expense;
    if (typeof x.id !== 'string') continue;
    try {
      const date = reviveDate(x.date as unknown);
      if (!date) continue;
      const createdAt = reviveDate(x.createdAt as unknown) ?? new Date(0);
      const updatedAt = reviveDate(x.updatedAt as unknown) ?? new Date(0);
      out.push({
        ...x,
        date,
        createdAt,
        updatedAt,
      });
    } catch {
      // skip
    }
  }
  return out;
}

/**
 * Firestore expense docs never include device-local receipt paths; the offline trip pack does.
 * Merge so Wallet/Plan lists keep `receiptLocalUri` after the live subscription overwrites state.
 */
export function mergeOfflineReceiptUrisIntoExpenses(live: Expense[], cached: Expense[]): Expense[] {
  if (!cached.length || !live.length) return live;
  const withLocal = new Map<string, string>();
  for (const e of cached) {
    const uri = e.receiptLocalUri?.trim();
    if (uri) withLocal.set(e.id, uri);
  }
  if (withLocal.size === 0) return live;
  return live.map((e) => {
    const local = withLocal.get(e.id);
    if (!local || e.receiptLocalUri) return e;
    return { ...e, receiptLocalUri: local };
  });
}

function reviveBookingDates(b: Booking): Booking {
  const out = { ...b } as Record<string, unknown>;
  const keys = [
    'checkInDate',
    'checkOutDate',
    'departureDate',
    'arrivalDate',
    'createdAt',
    'syncedAt',
    'rawEmailDate',
  ] as const;
  for (const k of keys) {
    const v = out[k];
    if (v != null && !(v instanceof Date)) {
      const d = new Date(v as string | number);
      if (Number.isFinite(d.getTime())) out[k] = d;
    }
  }
  if (out.flightStatus && typeof out.flightStatus === 'object') {
    const fs = { ...(out.flightStatus as object) } as Record<string, unknown>;
    for (const k of [
      'scheduledDeparture',
      'actualDeparture',
      'scheduledArrival',
      'actualArrival',
      'nextRefreshAt',
      'lastUpdated',
    ] as const) {
      const v = fs[k];
      if (v != null && !(v instanceof Date)) {
        const d = new Date(v as string | number);
        if (Number.isFinite(d.getTime())) fs[k] = d;
      }
    }
    out.flightStatus = fs;
  }
  return out as Booking;
}

/** Restore bookings from JSON/offline storage. */
export function bookingsFromOfflineCache(raw: unknown[] | undefined | null): Booking[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw
    .filter((row): row is object => Boolean(row) && typeof row === 'object')
    .filter((row) => typeof (row as Booking).id === 'string')
    .map((row) => reviveBookingDates(row as Booking));
}

